import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { API_URL, getEnvironmentInfo, apiRequest } from '../config/api';
import { formatCurrency, getCurrentTimestamp } from '../utils/dateUtils';
import { normalizeOrderType } from '../utils/orderTypeUtils';
import './styles/OrderReceipt.css';
import './styles/EmailInvoice.css';
// Environment-aware n8n webhook URL configuration for MULTI-invoice emails
const getN8nWebhookUrl = () => {
  const envInfo = getEnvironmentInfo();

  switch (envInfo.environment) {
    case 'development':
      return "https://gmc-space.app.n8n.cloud/webhook-test/send-multi-invoice-email";
    case 'production':
      return "https://gmc-space.app.n8n.cloud/webhook/send-multi-invoice-email";
    case 'staging':
    default:
      return "https://gmc-space.app.n8n.cloud/webhook/send-multi-invoice-email";
  }
};

const MultiOrderReceipt = ({
  orderNumbers = [],
  customerInfo: initialCustomerInfo,
  isVisible = false,
  onClose
}) => {
  const receiptRef = useRef();
  const [ordersData, setOrdersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Email state
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [emailProgress, setEmailProgress] = useState({ current: 0, total: 0 });
  const [emailCustomerInfo, setEmailCustomerInfo] = useState(null);
  const [customMessage, setCustomMessage] = useState('');

  const envInfo = getEnvironmentInfo();

  // State for shipping info
  const [shippingInfo, setShippingInfo] = useState(null);

  // Fetch all order data when component mounts or orderNumbers change
  useEffect(() => {
    const fetchAllOrdersData = async () => {
      if (!orderNumbers.length) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const ordersPromises = orderNumbers.map(async (orderNo) => {
          // Fetch order details, items, and payments in parallel
          // Use /api/orders/order/:orderno to get properly formatted data
          const [orderResponse, itemsResponse, paymentsResponse] = await Promise.all([
            fetch(`${API_URL}/api/orders/order/${orderNo}`),
            fetch(`${API_URL}/api/orders/${orderNo}/items`),
            fetch(`${API_URL}/api/orders/${orderNo}/payments`)
          ]);

          const orderDetails = await orderResponse.json();
          const itemsData = await itemsResponse.json();
          const paymentsData = await paymentsResponse.json();

          return {
            orderNo,
            orderDetails: orderDetails.order || orderDetails,
            items: itemsData.items || [],
            payments: paymentsData.payments || []
          };
        });

        const results = await Promise.all(ordersPromises);
        setOrdersData(results);

        // Fetch shipping info for the customer
        const customerId = initialCustomerInfo?.ID || initialCustomerInfo?.customerNo || results[0]?.orderDetails?.customerNo;
        if (customerId) {
          try {
            const shippingResponse = await fetch(`${API_URL}/api/customers/${customerId}/shipping`);
            const shippingData = await shippingResponse.json();
            setShippingInfo(shippingData.shippingAddress);
          } catch (err) {
            console.error('Error fetching shipping info:', err);
            setShippingInfo(null);
          }
        }
      } catch (err) {
        console.error('Error fetching orders data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (isVisible) {
      fetchAllOrdersData();
    }
  }, [orderNumbers, isVisible, initialCustomerInfo]);

  // Auto-print when data is loaded
  useEffect(() => {
    if (isVisible && !loading && ordersData.length > 0) {
      setTimeout(() => {
        handlePrint();
      }, 500);
    }
  }, [isVisible, loading, ordersData]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getCompanyInfo = (orderDetails) => {
    const warehouse = orderDetails?.orderWarehouse ||
      orderDetails?.warehouse ||
      orderDetails?.location ||
      'Pomona';

    switch (warehouse.toLowerCase()) {
      case 'lakewood':
        return {
          name: 'Dini Wigs',
          address: '1876 Lakewood Road',
          cityState: 'Toms River, NJ 08755',
          phone: '732.575.9941',
          website: 'WWW.DiniWigs.com'
        };
      case 'israel':
        return {
          name: 'Dini Wigs Israel',
          address: 'Hartom St 9',
          cityState: 'Jerusalem, Israel',
          phone: '+972-2-123-4567',
          website: 'WWW.DiniWigs.com'
        };
      default:
        return {
          name: 'Dini Wigs',
          address: '18 Thiells Mount Ivy Road',
          cityState: 'Pomona, NY 10970',
          phone: '(845) 262-1010',
          website: 'WWW.DiniWigs.com'
        };
    }
  };

  const calculateTotals = (items, payments) => {
    const subtotal = items.reduce((sum, item) => {
      const description = (item.OrderItemDescription || item.description || '').toLowerCase();
      if (description.includes('tax') || description.includes('processing fee') || description.includes('credit card fee')) {
        return sum;
      }
      return sum + parseFloat(item.OrderItemExtendedCost || item.extendedCost || item.price || 0);
    }, 0);

    const taxFromLineItems = items.reduce((sum, item) => {
      const description = (item.OrderItemDescription || item.description || '').toLowerCase();
      if (description.includes('tax applied') && !description.includes('credit card') && !description.includes('processing')) {
        return sum + parseFloat(item.OrderItemExtendedCost || item.extendedCost || item.price || 0);
      }
      return sum;
    }, 0);

    let regularPayments = 0;
    let taxPayments = 0;

    payments.forEach(payment => {
      const paymentType = (payment.PaymentType || '').toLowerCase();
      const paymentAmount = parseFloat(payment.PaymentAmount || payment.amount || 0);

      if (paymentType.includes('tax')) {
        taxPayments += paymentAmount;
      } else {
        regularPayments += paymentAmount;
      }
    });

    const totalPayments = regularPayments + taxPayments;
    const totalAmountDue = subtotal + taxFromLineItems - totalPayments;

    return {
      subtotal,
      taxAmount: taxFromLineItems,
      taxPayments,
      totalPayments: regularPayments,
      totalAmountDue: Math.max(0, totalAmountDue)
    };
  };

  const handlePrint = useCallback(() => {
    const printContent = receiptRef.current;

    if (!printContent) {
      console.error('Print content not found');
      return;
    }

    try {
      const printWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');

      if (!printWindow) {
        console.error('Failed to open print window - popup might be blocked');
        alert('Please allow popups for printing');
        return;
      }

      const printStyles = `
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Playfair+Display:wght@400;600;700&display=swap');

          @page {
            margin: 0.5in;
            size: letter;
          }

          :root {
            --dini-pink: #FF68C5;
            --dini-pink-light: #FF98D9;
            --dini-pink-dark: #E91E63;
            --text-dark: #2c2c2c;
            --text-light: #666;
            --border-light: #e0e0e0;
            --bg-white: #ffffff;
            --bg-light: #fafafa;
          }

          body {
            margin: 0 !important;
            padding: 0 !important;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
            background: var(--bg-white) !important;
          }

          .no-print {
            display: none !important;
          }

          .multi-receipt-wrapper {
            padding: 0;
          }

          .receipt-container {
            background: var(--bg-white) !important;
            color: var(--text-dark) !important;
            font-family: 'Inter', sans-serif !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 20px !important;
            line-height: 1.4 !important;
            font-size: 11px !important;
            position: relative !important;
            page-break-after: always !important;
          }

          .receipt-container:last-child {
            page-break-after: auto !important;
          }

          .modern-header {
            display: flex !important;
            justify-content: space-between !important;
            align-items: flex-start !important;
            margin-bottom: 25px !important;
            padding-bottom: 15px !important;
            border-bottom: 2px solid var(--dini-pink) !important;
            padding: 20px !important;
            margin: -20px -20px 25px -20px !important;
            border-radius: 0 0 12px 12px !important;
          }

          .company-info-left {
            font-family: 'Inter', monospace !important;
            font-size: 9px !important;
            line-height: 1.3 !important;
            color: var(--text-light) !important;
            font-weight: 400 !important;
            max-width: 200px !important;
          }

          .company-info-left strong {
            color: var(--text-dark) !important;
            font-weight: 600 !important;
          }

          .invoice-title-large {
            font-family: 'Playfair Display', serif !important;
            font-size: 38px !important;
            font-weight: 700 !important;
            color: var(--text-dark) !important;
            margin: 0 !important;
            letter-spacing: -1.2px !important;
            line-height: 0.85 !important;
            text-shadow: 1px 1px 0 var(--dini-pink-light) !important;
          }

          .details-billing-container {
            display: flex !important;
            gap: 20px !important;
            margin-bottom: 20px !important;
          }

          .invoice-details {
            flex: 1 !important;
            font-family: 'Inter', monospace !important;
            font-size: 12px !important;
            line-height: 1.4 !important;
            background: var(--bg-light) !important;
            padding: 12px !important;
            border-radius: 6px !important;
            border-left: 3px solid var(--dini-pink) !important;
          }

          .invoice-details div {
            margin-bottom: 2px !important;
            display: flex !important;
            justify-content: space-between !important;
          }

          .invoice-details strong {
            color: var(--text-dark) !important;
            font-weight: 600 !important;
          }

          .bill-to-section {
            flex: 1 !important;
            font-family: 'Inter', monospace !important;
            font-size: 12px !important;
            line-height: 1.3 !important;
            background: var(--bg-white) !important;
            border: 1px solid var(--border-light) !important;
            padding: 12px !important;
            border-radius: 6px !important;
          }

          .bill-to-title {
            font-weight: 700 !important;
            margin-bottom: 8px !important;
            font-size: 10px !important;
            color: var(--text-dark) !important;
            letter-spacing: 0.3px !important;
            text-transform: uppercase !important;
            border-bottom: 1px solid var(--dini-pink-light) !important;
            padding-bottom: 4px !important;
          }

          .modern-table {
            width: 100% !important;
            border-collapse: collapse !important;
            margin-bottom: 20px !important;
            border-radius: 12px !important;
            overflow: hidden !important;
            box-shadow: 0 2px 8px rgba(26, 26, 26, 0.1) !important;
          }

          .modern-table th {
            background: linear-gradient(135deg, var(--dini-pink), var(--dini-pink-dark)) !important;
            color: white !important;
            padding: 10px 8px !important;
            font-weight: 600 !important;
            font-family: 'Inter', monospace !important;
            font-size: 10px !important;
            text-align: left !important;
            letter-spacing: 0.5px !important;
            text-transform: uppercase !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .modern-table th:last-child {
            text-align: right !important;
          }

          .modern-table td {
            padding: 8px !important;
            border-bottom: 1px solid var(--border-light) !important;
            font-family: 'Inter', monospace !important;
            font-size: 10px !important;
            vertical-align: top !important;
            background: white !important;
          }

          .modern-table td:last-child {
            text-align: right !important;
            font-weight: 600 !important;
            color: var(--text-dark) !important;
          }

          .totals-section-modern {
            margin-top: 15px !important;
            padding: 15px !important;
            background: var(--bg-light) !important;
            border: 1px solid var(--border-light) !important;
            border-radius: 8px !important;
            max-width: 250px !important;
            margin-left: auto !important;
          }

          .total-line-modern {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            margin-bottom: 4px !important;
            font-family: 'Inter', monospace !important;
            font-size: 10px !important;
            padding: 2px 0 !important;
          }

          .total-line-modern span:first-child {
            color: var(--text-light) !important;
            font-weight: 500 !important;
          }

          .total-line-modern span:last-child {
            font-weight: 600 !important;
            color: var(--text-dark) !important;
          }

          .total-line-modern.final {
            font-weight: 700 !important;
            font-size: 12px !important;
            margin-top: 8px !important;
            padding-top: 8px !important;
            border-top: 2px solid var(--dini-pink) !important;
            color: var(--text-dark) !important;
            background: var(--dini-pink-light) !important;
            padding: 8px 6px 6px 6px !important;
            border-radius: 4px !important;
          }

          .total-line-modern.final span {
            color: var(--text-dark) !important;
            font-weight: 700 !important;
          }

          .warranty-section {
            margin: 10px 0 !important;
            padding: 8px !important;
            background: var(--bg-light) !important;
            border: 1px solid var(--border-light) !important;
            border-radius: 4px !important;
            font-family: 'Inter', monospace !important;
            font-size: 7px !important;
            line-height: 1.2 !important;
            text-align: center !important;
            page-break-inside: avoid !important;
          }

          .warranty-title {
            font-family: 'Playfair Display', serif !important;
            font-weight: 600 !important;
            margin-bottom: 4px !important;
            font-size: 12px !important;
            color: var(--text-dark) !important;
            font-style: italic !important;
          }

          .warranty-text {
            color: var(--text-dark) !important;
            font-style: italic !important;
            font-size: 10px !important;
            max-width: 800px !important;
            margin: 0 auto !important;
            line-height: 1.1 !important;
          }
        </style>
      `;

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Invoices - ${orderNumbers.length} Orders</title>
          <meta charset="utf-8">
          ${printStyles}
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
        </html>
      `;

      printWindow.document.open();
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      setTimeout(() => {
        try {
          printWindow.focus();
          setTimeout(() => {
            printWindow.print();
            setTimeout(() => {
              if (printWindow && !printWindow.closed) {
                printWindow.close();
              }
            }, 1000);
          }, 1000);
        } catch (printError) {
          console.error('Error during printing:', printError);
          alert('Error occurred while preparing print. Please try again.');
        }
      }, 100);

    } catch (error) {
      console.error('Error in print function:', error);
      alert('Error occurred while preparing print. Please try again.');
    }
  }, [orderNumbers]);

  // Initialize email customer info when email modal opens
  useEffect(() => {
    if (isEmailModalOpen && initialCustomerInfo) {
      setEmailCustomerInfo({
        CustomerEmail: initialCustomerInfo.CustomerEmail || '',
        CustomerFullName: initialCustomerInfo.CustomerFullName ||
          `${initialCustomerInfo.CustomerFirstName || ''} ${initialCustomerInfo.CustomerLastName || ''}`.trim(),
        CustomerPhone: initialCustomerInfo.CustomerPhone || '',
      });
    }
  }, [isEmailModalOpen, initialCustomerInfo]);

  // Generate HTML for a single receipt (for email)
  const generateReceiptHtmlForOrder = async (orderData) => {
    const { orderNo, orderDetails, items, payments } = orderData;
    const companyInfo = getCompanyInfo(orderDetails);
    const totals = calculateTotals(items, payments);
    const currentDate = new Date().toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });

    // Load logo as base64
    let logoBase64 = '';
    try {
      const logoResponse = await fetch('/images/dini_title.png');
      const logoBlob = await logoResponse.blob();
      logoBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(logoBlob);
      });
    } catch (err) {
      console.warn('Could not load logo:', err);
    }

    const printStyles = `
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Playfair+Display:wght@400;600;700&display=swap');
        @page { margin: 0.5in; size: letter; }
        :root { --dini-pink: #FF68C5; --dini-pink-light: #FF98D9; --dini-pink-dark: #E91E63; --text-dark: #000000; --text-light: #333333; --border-light: #e0e0e0; --bg-white: #ffffff; --bg-light: #fafafa; }
        body { margin: 0 !important; padding: 0 !important; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important; background: var(--bg-white) !important; }
        .receipt-container { background: var(--bg-white) !important; color: var(--text-dark) !important; font-family: 'Inter', sans-serif !important; max-width: 100% !important; margin: 0 !important; padding: 20px !important; line-height: 1.5 !important; font-size: 13px !important; }
        .modern-header { display: flex !important; justify-content: space-between !important; align-items: flex-start !important; margin-bottom: 25px !important; padding-bottom: 15px !important; border-bottom: 2px solid var(--dini-pink) !important; padding: 20px !important; margin: -20px -20px 25px -20px !important; }
        .company-info-left { font-family: 'Inter', sans-serif !important; font-size: 11px !important; line-height: 1.4 !important; color: var(--text-dark) !important; }
        .invoice-title-large { font-family: 'Playfair Display', serif !important; font-size: 42px !important; font-weight: 700 !important; color: var(--text-dark) !important; margin: 0 !important; }
        .details-billing-container { display: flex !important; gap: 20px !important; margin-bottom: 20px !important; }
        .invoice-details { flex: 1 !important; font-size: 13px !important; background: var(--bg-light) !important; padding: 12px !important; border-radius: 6px !important; border-left: 3px solid var(--dini-pink) !important; }
        .invoice-details div { margin-bottom: 3px !important; display: flex !important; justify-content: space-between !important; }
        .invoice-details strong { color: var(--text-dark) !important; font-weight: 600 !important; }
        .bill-to-section { flex: 1 !important; font-size: 13px !important; background: var(--bg-white) !important; border: 1px solid var(--border-light) !important; padding: 12px !important; border-radius: 6px !important; }
        .bill-to-title { font-weight: 700 !important; margin-bottom: 8px !important; font-size: 11px !important; text-transform: uppercase !important; border-bottom: 1px solid var(--dini-pink-light) !important; padding-bottom: 4px !important; }
        .modern-table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 20px !important; }
        .modern-table th { background: linear-gradient(135deg, var(--dini-pink), var(--dini-pink-dark)) !important; color: white !important; padding: 12px 10px !important; font-weight: 600 !important; font-size: 11px !important; text-align: left !important; text-transform: uppercase !important; }
        .modern-table th:last-child { text-align: right !important; }
        .modern-table td { padding: 10px !important; border-bottom: 1px solid var(--border-light) !important; font-size: 12px !important; background: white !important; color: var(--text-dark) !important; }
        .modern-table td:last-child { text-align: right !important; font-weight: 600 !important; }
        .totals-section-modern { margin-top: 15px !important; padding: 15px !important; background: var(--bg-light) !important; border: 1px solid var(--border-light) !important; border-radius: 8px !important; max-width: 280px !important; margin-left: auto !important; }
        .total-line-modern { display: flex !important; justify-content: space-between !important; margin-bottom: 5px !important; font-size: 12px !important; }
        .total-line-modern.final { font-weight: 700 !important; font-size: 14px !important; margin-top: 10px !important; padding-top: 10px !important; border-top: 2px solid var(--dini-pink) !important; background: var(--dini-pink-light) !important; padding: 10px 8px !important; border-radius: 4px !important; }
        .warranty-section { margin: 10px 0 !important; padding: 10px !important; background: var(--bg-light) !important; border: 1px solid var(--border-light) !important; border-radius: 4px !important; font-size: 9px !important; text-align: center !important; }
        .warranty-title { font-weight: 600 !important; margin-bottom: 6px !important; font-size: 13px !important; font-style: italic !important; }
        .warranty-text { font-style: italic !important; font-size: 11px !important; line-height: 1.3 !important; }
      </style>
    `;

    const warrantySection = ['Wig Repair', 'New Wig', 'New Sale Wig'].includes(normalizeOrderType(orderDetails?.orderType || orderDetails?.OrderWigType)) ? `
      <div class="warranty-section">
        <div class="warranty-title">Important Policy Information</div>
        <div class="warranty-text">
          Please be aware of the following policies: Prices are subject to change as we inspect the repair.
          Please be advised that a Dini Wigs representative may call you before we begin alterations in case
          of pricing discrepancies. All paid alterations include wash and set in the cost. All alterations
          covered by the warranty are subject to either wash and set or touch-up charge. All Prices are
          subject to applicable taxes. A 3% processing fee is applied to credit card purchases
          exceeding $150. If you would like to request for your wig not to be washed please let our staff know.
          For more information please check our policy on our website at www.diniwigs.com/terms-and-conditions-of-purchase
        </div>
      </div>
    ` : '';

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - Order #${orderNo}</title>
          <meta charset="utf-8">
          ${printStyles}
        </head>
        <body>
          <div class="receipt-container">
            <div class="modern-header">
              <div class="company-info-left">
                <div>${companyInfo.address}, ${companyInfo.cityState}</div>
                <div>${companyInfo.phone}</div>
                <div>${companyInfo.website}</div>
                <div class="invoice-title-large">INVOICE</div>
              </div>
              <div class="company-logo-right">
                ${logoBase64 ? `<img src="${logoBase64}" alt="Company Logo" style="width: 150px; height: 100px;" />` : ''}
              </div>
            </div>

            <div class="details-billing-container">
              <div class="invoice-details">
                <div><strong>Issued:</strong> ${formatDate(orderDetails?.orderDate || orderDetails?.OrderDate)}</div>
                <div><strong>Order Number:</strong> ${orderDetails?.orderNo || orderNo}</div>
                <div><strong>Location:</strong> ${orderDetails?.warehouse || orderDetails?.location || orderDetails?.orderWarehouse || 'Pomona'}</div>
                <div><strong>Order Type:</strong> ${orderDetails?.orderType || orderDetails?.OrderWigType}</div>
                <div><strong>Due Date:</strong> ${formatDate(orderDetails?.dueDate || orderDetails?.DueDate)}</div>
                <div><strong>Wig Code:</strong> ${orderDetails?.wigBarCode || orderDetails?.Wig_Bar_Code || ''}</div>
              </div>

              <div class="bill-to-section">
                <div class="bill-to-title">BILL TO:</div>
                <div>${initialCustomerInfo?.CustomerFullName || orderDetails?.customerName || orderDetails?.OrderCustomerName || 'Customer'}</div>
                <div>${initialCustomerInfo?.CustomerPhone || orderDetails?.customerPhone || orderDetails?.OrderPhone || ''}</div>
                <div>${initialCustomerInfo?.CustomerAddress || orderDetails?.customerAddress || orderDetails?.OrderCustomerAddress || ''}</div>
                <div>${[
                  initialCustomerInfo?.CustomerCity || orderDetails?.customerCity || orderDetails?.OrderCity,
                  initialCustomerInfo?.CustomerState || orderDetails?.customerState || orderDetails?.OrderSt,
                  initialCustomerInfo?.CustomerZipCode || orderDetails?.customerZip || orderDetails?.OrderZipCode
                ].filter(Boolean).join(', ')}</div>
              </div>

              <div class="bill-to-section" style="margin-top: 8px;">
                <div class="bill-to-title">SHIP TO:</div>
                ${shippingInfo ? `
                  <div>${initialCustomerInfo?.CustomerFullName || orderDetails?.customerName || orderDetails?.OrderCustomerName || ''}</div>
                  <div>${initialCustomerInfo?.CustomerPhone || orderDetails?.customerPhone || orderDetails?.OrderPhone || ''}</div>
                  <div style="white-space: pre-line;">${shippingInfo}</div>
                ` : `
                  <div>${initialCustomerInfo?.CustomerFullName || orderDetails?.customerName || orderDetails?.OrderCustomerName || ''}</div>
                  <div>${initialCustomerInfo?.CustomerPhone || orderDetails?.customerPhone || orderDetails?.OrderPhone || ''}</div>
                  <div>${initialCustomerInfo?.CustomerAddress || orderDetails?.customerAddress || orderDetails?.OrderCustomerAddress || 'Address not available'}</div>
                  <div>${[
                    initialCustomerInfo?.CustomerCity || orderDetails?.customerCity || orderDetails?.OrderCity,
                    initialCustomerInfo?.CustomerState || orderDetails?.customerState || orderDetails?.OrderSt,
                    initialCustomerInfo?.CustomerZipCode || orderDetails?.customerZip || orderDetails?.OrderZipCode
                  ].filter(Boolean).join(', ') || 'City, State, Zip not available'}</div>
                `}
              </div>
            </div>

            <table class="modern-table">
              <thead>
                <tr>
                  <th style="width: 40%">DESCRIPTION</th>
                  <th style="width: 15%">DATE</th>
                  <th style="width: 10%">QTY</th>
                  <th style="width: 20%">PRICE</th>
                  <th style="width: 15%">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                ${items.map(item => `
                  <tr>
                    <td>${item.OrderItemDescription || item.description || ''}</td>
                    <td>${formatDate(item.OrderItemDate || item.date)}</td>
                    <td>${item.OrderQty || item.quantity || 1}</td>
                    <td>${formatCurrency(item.OrderItemRetailCost || item.price || item.retailCost)}</td>
                    <td>${formatCurrency(item.OrderItemExtendedCost || item.extendedCost || 0)}</td>
                  </tr>
                `).join('')}
                ${payments.map(payment => {
                  const paymentType = (payment.PaymentType || '').toLowerCase();
                  const paymentAmount = parseFloat(payment.PaymentAmount || payment.amount || 0);
                  const paymentMethod = payment.PaymentMethod || payment.method || 'Unknown';
                  const paymentDate = payment.PaymentDate || payment.date;
                  const displayLabel = paymentType.includes('tax')
                    ? (payment.PaymentType || `Tax Payment - ${paymentMethod}`)
                    : (payment.PaymentType || `Payment - ${paymentMethod}`);
                  return `
                    <tr>
                      <td>${displayLabel}</td>
                      <td>${formatDate(paymentDate)}</td>
                      <td>1</td>
                      <td>(${formatCurrency(paymentAmount)})</td>
                      <td>(${formatCurrency(paymentAmount)})</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>

            <div class="totals-section-modern">
              <div class="total-line-modern">
                <span>Purchase Subtotal:</span>
                <span>${formatCurrency(totals.subtotal)}</span>
              </div>
              ${totals.taxAmount > 0 ? `
                <div class="total-line-modern">
                  <span>Tax:</span>
                  <span>${formatCurrency(totals.taxAmount)}</span>
                </div>
              ` : ''}
              ${totals.taxPayments > 0 ? `
                <div class="total-line-modern">
                  <span>Tax Paid to Date:</span>
                  <span>${formatCurrency(totals.taxPayments)}</span>
                </div>
              ` : ''}
              <div class="total-line-modern">
                <span>Total Payments to Date: (${currentDate})</span>
                <span>(${formatCurrency(totals.totalPayments)})</span>
              </div>
              <div class="total-line-modern final">
                <span>TOTAL AMOUNT DUE:</span>
                <span>${formatCurrency(totals.totalAmountDue)}</span>
              </div>
            </div>

            ${warrantySection}
          </div>
        </body>
      </html>
    `;
  };

  // Generate PDF for a single order (returns attachment object)
  const generatePdfForOrder = async (orderData) => {
    const { orderNo } = orderData;

    // Generate HTML for this invoice
    const invoiceHtml = await generateReceiptHtmlForOrder(orderData);

    // Generate PDF via backend
    const pdfResponse = await apiRequest(`${envInfo.apiUrl}/api/invoice/generate-pdf-base64`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        invoiceHtml: invoiceHtml,
        orderNumber: orderNo,
        options: {
          format: 'Letter',
          margin: { top: '0.5in', right: '0.5in', bottom: '0.5in', left: '0.5in' }
        }
      })
    });

    if (!pdfResponse.ok) {
      throw new Error(`Failed to generate PDF for order ${orderNo}`);
    }

    const pdfData = await pdfResponse.json();
    if (!pdfData.success) {
      throw new Error(pdfData.message || `Failed to generate PDF for order ${orderNo}`);
    }

    return {
      orderNo,
      filename: pdfData.filename,
      content: pdfData.pdfBase64,
      encoding: 'base64',
      contentType: 'application/pdf'
    };
  };

  // Handle sending ONE email with ALL invoices as attachments
  const handleSendAllEmails = async () => {
    if (!emailCustomerInfo?.CustomerEmail) {
      setEmailError('Customer email address is required');
      return;
    }

    setIsSendingEmail(true);
    setEmailError(null);
    setEmailProgress({ current: 0, total: ordersData.length, phase: 'generating' });

    const pdfAttachments = [];
    const orderNumbers = [];
    const errors = [];

    // Phase 1: Generate all PDFs
    for (let i = 0; i < ordersData.length; i++) {
      try {
        setEmailProgress({ current: i + 1, total: ordersData.length, phase: 'generating' });
        const pdfAttachment = await generatePdfForOrder(ordersData[i]);
        pdfAttachments.push(pdfAttachment);
        orderNumbers.push(ordersData[i].orderNo);
      } catch (err) {
        console.error(`Error generating PDF for order ${ordersData[i].orderNo}:`, err);
        errors.push({ orderNo: ordersData[i].orderNo, error: err.message });
      }
    }

    // If any PDF generation failed, show error
    if (errors.length > 0) {
      setIsSendingEmail(false);
      setEmailError(`Failed to generate PDFs for: ${errors.map(e => `#${e.orderNo}`).join(', ')}`);
      return;
    }

    // Phase 2: Send single email with all attachments
    setEmailProgress({ current: 0, total: 1, phase: 'sending' });

    try {
      const webhookUrl = getN8nWebhookUrl();
      const firstOrder = ordersData[0]?.orderDetails;

      const emailData = {
        customerEmail: emailCustomerInfo.CustomerEmail,
        customerName: emailCustomerInfo.CustomerFullName || 'Valued Customer',
        customerId: firstOrder?.customerNo || firstOrder?.OrderCustomerNo,
        orderNumbers: orderNumbers, // Array of all order numbers
        orderCount: orderNumbers.length,
        // Array of PDF attachments (instead of single pdfAttachment)
        pdfAttachments: pdfAttachments.map(att => ({
          filename: att.filename,
          content: att.content,
          encoding: att.encoding,
          contentType: att.contentType
        })),
        customMessage: customMessage.trim(),
        environment: envInfo.environment,
        sentAt: getCurrentTimestamp(),
        sentFrom: 'DiniTracker Application - Multi-Invoice Email',
        routing: {
          environment: envInfo.environment,
          hostname: envInfo.hostname,
          timestamp: getCurrentTimestamp()
        },
        customerInfo: emailCustomerInfo
      };

      const response = await apiRequest(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Environment': envInfo.environment,
          'X-Source': 'DiniTracker-MultiEmailInvoice'
        },
        body: JSON.stringify(emailData)
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // Ignore parse error
        }
        throw new Error(errorMessage);
      }

      setIsSendingEmail(false);
      alert(`Successfully sent ${pdfAttachments.length} invoice${pdfAttachments.length > 1 ? 's' : ''} to ${emailCustomerInfo.CustomerEmail}!`);
      setIsEmailModalOpen(false);
      setCustomMessage('');

    } catch (err) {
      console.error('Error sending email:', err);
      setIsSendingEmail(false);
      setEmailError(`Failed to send email: ${err.message}`);
    }
  };

  const handleOpenEmailModal = () => {
    if (!initialCustomerInfo) {
      alert('No customer information available');
      return;
    }
    setIsEmailModalOpen(true);
  };

  const handleCloseEmailModal = () => {
    setIsEmailModalOpen(false);
    setEmailError(null);
    setCustomMessage('');
  };

  if (!isVisible) return null;

  const renderSingleReceipt = (orderData) => {
    const { orderNo, orderDetails, items, payments } = orderData;
    const companyInfo = getCompanyInfo(orderDetails);
    const totals = calculateTotals(items, payments);
    const currentDate = new Date().toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });

    return (
      <div key={orderNo} className="receipt-container">
        {/* Modern Header */}
        <div className="modern-header">
          <div className="company-info-left">
            <div>{companyInfo.address}, {companyInfo.cityState}</div>
            <div>{companyInfo.phone}</div>
            <div>{companyInfo.website}</div>
            <div className="invoice-title-large">INVOICE</div>
          </div>
          <div className="company-logo-right">
            <img
              src="/images/dini_title.png"
              alt="Company Logo"
              style={{ width: '150px', height: '100px' }}
            />
          </div>
        </div>

        <div className="details-billing-container">
          {/* Invoice Details */}
          <div className="invoice-details">
            <div><strong>Issued:</strong> {formatDate(orderDetails?.orderDate || orderDetails?.OrderDate)}</div>
            <div><strong>Order Number:</strong> {orderDetails?.orderNo || orderNo}</div>
            <div><strong>Location:</strong> {orderDetails?.warehouse || orderDetails?.location || orderDetails?.orderWarehouse || 'Pomona'}</div>
            <div><strong>Order Type:</strong> {orderDetails?.orderType || orderDetails?.OrderWigType}</div>
            <div><strong>Due Date:</strong> {formatDate(orderDetails?.dueDate || orderDetails?.DueDate)}</div>
            <div><strong>Wig Code:</strong> {orderDetails?.wigBarCode || orderDetails?.Wig_Bar_Code}</div>
          </div>

          {/* Bill To Section */}
          <div className="bill-to-section">
            <div className="bill-to-title">BILL TO:</div>
            <div>
              {initialCustomerInfo?.CustomerFullName ||
                `${initialCustomerInfo?.CustomerFirstName || ''} ${initialCustomerInfo?.CustomerLastName || ''}`.trim() ||
                orderDetails?.customerName ||
                orderDetails?.OrderCustomerName ||
                'Customer name not available'}
            </div>
            <div>
              {initialCustomerInfo?.CustomerPhone ||
                orderDetails?.customerPhone ||
                orderDetails?.OrderPhone ||
                'Phone not available'}
            </div>
            <div>
              {initialCustomerInfo?.CustomerAddress ||
                orderDetails?.customerAddress ||
                orderDetails?.OrderCustomerAddress ||
                'Address not available'}
            </div>
            <div>
              {[
                initialCustomerInfo?.CustomerCity || orderDetails?.customerCity || orderDetails?.OrderCity,
                initialCustomerInfo?.CustomerState || orderDetails?.customerState || orderDetails?.OrderSt || orderDetails?.OrderState,
                initialCustomerInfo?.CustomerZipCode || orderDetails?.customerZip || orderDetails?.OrderZipCode || orderDetails?.OrderZip
              ].filter(Boolean).join(', ') || 'City, State, Zip not available'}
            </div>
          </div>

          {/* Ship To Section */}
          <div className="bill-to-section" style={{ marginTop: 8 }}>
            <div className="bill-to-title">SHIP TO:</div>
            {shippingInfo ? (
              <>
                <div>
                  {initialCustomerInfo?.CustomerFullName ||
                    `${initialCustomerInfo?.CustomerFirstName || ''} ${initialCustomerInfo?.CustomerLastName || ''}`.trim() ||
                    orderDetails?.customerName ||
                    orderDetails?.OrderCustomerName}
                </div>
                <div>
                  {initialCustomerInfo?.CustomerPhone ||
                    orderDetails?.customerPhone ||
                    orderDetails?.OrderPhone}
                </div>
                <div style={{ whiteSpace: 'pre-line' }}>{shippingInfo}</div>
              </>
            ) : (
              // Fallback to billing address when no shipping address
              <>
                <div>
                  {initialCustomerInfo?.CustomerFullName ||
                    `${initialCustomerInfo?.CustomerFirstName || ''} ${initialCustomerInfo?.CustomerLastName || ''}`.trim() ||
                    orderDetails?.customerName ||
                    orderDetails?.OrderCustomerName}
                </div>
                <div>
                  {initialCustomerInfo?.CustomerPhone ||
                    orderDetails?.customerPhone ||
                    orderDetails?.OrderPhone}
                </div>
                <div>
                  {initialCustomerInfo?.CustomerAddress ||
                    orderDetails?.customerAddress ||
                    orderDetails?.OrderCustomerAddress ||
                    'Address not available'}
                </div>
                <div>
                  {[
                    initialCustomerInfo?.CustomerCity || orderDetails?.customerCity || orderDetails?.OrderCity,
                    initialCustomerInfo?.CustomerState || orderDetails?.customerState || orderDetails?.OrderSt || orderDetails?.OrderState,
                    initialCustomerInfo?.CustomerZipCode || orderDetails?.customerZip || orderDetails?.OrderZipCode || orderDetails?.OrderZip
                  ].filter(Boolean).join(', ') || 'City, State, Zip not available'}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Items Table */}
        <table className="modern-table">
          <thead>
            <tr>
              <th style={{ width: '40%' }}>DESCRIPTION</th>
              <th style={{ width: '15%' }}>DATE</th>
              <th style={{ width: '10%' }}>QTY</th>
              <th style={{ width: '20%' }}>PRICE</th>
              <th style={{ width: '15%' }}>TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={`item-${index}`}>
                <td>{item.OrderItemDescription || item.description}</td>
                <td>{formatDate(item.OrderItemDate || item.date)}</td>
                <td>{item.OrderQty || item.quantity || 1}</td>
                <td>{formatCurrency(item.OrderItemRetailCost || item.price || item.retailCost)}</td>
                <td>{formatCurrency(item.OrderItemExtendedCost || item.extendedCost || ((item.price || 0) * (item.quantity || 1)))}</td>
              </tr>
            ))}
            {/* Payment Records */}
            {payments.map((payment, index) => {
              const paymentType = (payment.PaymentType || '').toLowerCase();
              const paymentAmount = parseFloat(payment.PaymentAmount || payment.amount || 0);
              const paymentMethod = payment.PaymentMethod || payment.method || 'Unknown';
              const paymentDate = payment.PaymentDate || payment.date;

              if (paymentType.includes('tax')) {
                return (
                  <tr key={`tax-payment-${index}`}>
                    <td>Tax Payment - {paymentMethod}</td>
                    <td>{formatDate(paymentDate)}</td>
                    <td>1</td>
                    <td>({formatCurrency(paymentAmount)})</td>
                    <td>({formatCurrency(paymentAmount)})</td>
                  </tr>
                );
              }
              return (
                <tr key={`payment-${index}`}>
                  <td>{payment.PaymentType || `Payment - ${paymentMethod}`}</td>
                  <td>{formatDate(paymentDate)}</td>
                  <td>1</td>
                  <td>({formatCurrency(paymentAmount)})</td>
                  <td>({formatCurrency(paymentAmount)})</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Totals Section */}
        <div className="totals-section-modern">
          <div className="total-line-modern">
            <span>Purchase Subtotal:</span>
            <span>{formatCurrency(totals.subtotal)}</span>
          </div>
          <div className="total-line-modern">
            <span style={{ fontSize: '0.8em', color: 'var(--text-light)', fontStyle: 'italic' }}>
              (tax not included)
            </span>
            <span></span>
          </div>

          {totals.taxAmount > 0 && (
            <div className="total-line-modern">
              <span>Tax:</span>
              <span>{formatCurrency(totals.taxAmount)}</span>
            </div>
          )}

          {totals.taxPayments > 0 && (
            <div className="total-line-modern">
              <span>Tax Paid to Date:</span>
              <span>{formatCurrency(totals.taxPayments)}</span>
            </div>
          )}

          <div className="total-line-modern">
            <span>Total Payments to Date: ({currentDate})</span>
            <span>({formatCurrency(totals.totalPayments)})</span>
          </div>

          <div className="total-line-modern final">
            <span>TOTAL AMOUNT DUE:</span>
            <span>{formatCurrency(totals.totalAmountDue)}</span>
          </div>
        </div>

        {/* Warranty Information */}
        {['Wig Repair', 'New Wig', 'New Sale Wig'].includes(normalizeOrderType(orderDetails?.orderType || orderDetails?.OrderWigType)) && (
          <div className="warranty-section">
            <div className="warranty-title">Important Policy Information</div>
            <div className="warranty-text">
              Please be aware of the following policies: Prices are subject to change as we inspect the repair.
              Please be advised that a Dini Wigs representative may call you before we begin alterations in case
              of pricing discrepancies. All paid alterations include wash and set in the cost. All alterations
              covered by the warranty are subject to either wash and set or touch-up charge. All Prices are
              subject to applicable taxes. A 3% processing fee is applied to credit card purchases
              exceeding $150. If you would like to request for your wig not to be washed please let our staff know.
              For more information please check our policy on our website at www.diniwigs.com/terms-and-conditions-of-purchase
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="modal show d-block"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.61)',
        zIndex: 1050,
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'auto'
      }}
      onClick={onClose}
    >
      <div className="modal-dialog modal-xl" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header no-print">
            <h5 className="modal-title">
              <i className="fas fa-receipt mr-2"></i>
              {orderNumbers.length} Receipt{orderNumbers.length > 1 ? 's' : ''} Selected
            </h5>
            <div>
              <button
                className="btn btn-primary mr-2"
                onClick={handlePrint}
                disabled={loading}
              >
                <i className="fas fa-print mr-1"></i>
                Print All
              </button>
              <button
                className="btn btn-warning mr-2"
                onClick={handleOpenEmailModal}
                disabled={loading || !initialCustomerInfo}
              >
                <i className="fas fa-envelope mr-1"></i>
                Email All
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                <i className="fas fa-times mr-1"></i>
                Close
              </button>
            </div>
          </div>

          <div className="modal-body p-6">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <p className="mt-3">Loading receipts...</p>
              </div>
            ) : error ? (
              <div className="alert alert-danger">
                <strong>Error:</strong> {error}
              </div>
            ) : (
              <div ref={receiptRef} className="multi-receipt-wrapper">
                {ordersData.map((orderData) => renderSingleReceipt(orderData))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Email Modal */}
      {isEmailModalOpen && createPortal(
        <div className="email-modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="email-modal">
            <div className="email-modal-header">
              <h3>
                <i className="fas fa-envelope" style={{ marginRight: '8px' }}></i>
                Email {ordersData.length} Invoice{ordersData.length > 1 ? 's' : ''}
              </h3>
              <button
                onClick={handleCloseEmailModal}
                className="close-button"
                aria-label="Close"
                disabled={isSendingEmail}
              >
                ×
              </button>
            </div>

            <div className="email-modal-content">
              {emailError ? (
                <div className="error-state">
                  <p className="error-message">❌ {emailError}</p>
                  <button onClick={() => setEmailError(null)} className="retry-button">
                    Try Again
                  </button>
                </div>
              ) : isSendingEmail ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p style={{ marginBottom: '16px' }}>
                    {emailProgress.phase === 'generating'
                      ? `Generating PDF ${emailProgress.current} of ${emailProgress.total}...`
                      : 'Sending email with all attachments...'}
                  </p>
                  <div style={{
                    width: '100%',
                    height: '24px',
                    backgroundColor: '#e9ecef',
                    borderRadius: '12px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: emailProgress.phase === 'generating'
                        ? `${(emailProgress.current / emailProgress.total) * 100}%`
                        : '100%',
                      height: '100%',
                      background: emailProgress.phase === 'generating'
                        ? 'linear-gradient(135deg, #FF68C5, #E91E63)'
                        : 'linear-gradient(135deg, #28a745, #20c997)',
                      borderRadius: '12px',
                      transition: 'width 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '12px'
                    }}>
                      {emailProgress.phase === 'generating'
                        ? `${emailProgress.current}/${emailProgress.total}`
                        : '📧 Sending...'}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="email-form">
                  <div className="customer-info-email">
                    <div className="info-row">
                      <label>To:</label>
                      <input
                        type="email"
                        value={emailCustomerInfo?.CustomerEmail || ''}
                        onChange={(e) => setEmailCustomerInfo(prev => ({
                          ...prev,
                          CustomerEmail: e.target.value
                        }))}
                        placeholder="customer@example.com"
                        className="customer-input"
                        required
                      />
                    </div>
                    <div className="info-row">
                      <label>Customer:</label>
                      <input
                        type="text"
                        value={emailCustomerInfo?.CustomerFullName || ''}
                        onChange={(e) => setEmailCustomerInfo(prev => ({
                          ...prev,
                          CustomerFullName: e.target.value
                        }))}
                        placeholder="Customer Name"
                        className="customer-input"
                      />
                    </div>
                    <div className="info-row">
                      <label>Orders:</label>
                      <span>{ordersData.length} invoice{ordersData.length > 1 ? 's' : ''} selected</span>
                    </div>

                    {!envInfo.isProduction && (
                      <div className="info-row environment-info">
                        <label>Environment:</label>
                        <span className={`env-badge env-${envInfo.environment}`}>
                          {envInfo.environment.toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Orders List */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#495057', marginBottom: '8px' }}>
                      Orders to Send:
                    </label>
                    <div style={{
                      maxHeight: '100px',
                      overflowY: 'auto',
                      border: '1px solid #e9ecef',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      backgroundColor: '#f8f9fa'
                    }}>
                      {ordersData.map((order, idx) => (
                        <div key={idx} style={{ fontSize: '13px', padding: '2px 0', color: '#495057' }}>
                          <strong>#{order.orderNo}</strong> - {order.orderDetails?.orderType || 'Order'}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Custom Message */}
                  <div className="custom-message">
                    <label htmlFor="customMessageMulti">Custom Message (Optional):</label>
                    <textarea
                      id="customMessageMulti"
                      placeholder="Add a personal message to include with the invoices..."
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      rows="3"
                      maxLength="500"
                    />
                    <small>{customMessage.length}/500 characters</small>
                  </div>

                  {/* Action Buttons */}
                  <div className="modal-actions">
                    <button
                      onClick={handleCloseEmailModal}
                      className="cancel-button"
                      disabled={isSendingEmail}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendAllEmails}
                      className="send-button"
                      disabled={isSendingEmail || !emailCustomerInfo?.CustomerEmail}
                    >
                      {isSendingEmail ? (
                        <>
                          <div className="spinner small"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane"></i>
                          Send {ordersData.length} Invoice{ordersData.length > 1 ? 's' : ''}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default MultiOrderReceipt;
